/**
 * Remittance & Hire Purchase data access.
 *
 * Types come from the shared contract rather than being restated here, so a
 * field the API stops sending is a compile error instead of an `undefined` in
 * a table cell.
 *
 * Every mutation invalidates the boards it could have changed, and the
 * hire purchase mutations deliberately do NOT invalidate the remittance board:
 * the two ledgers are separate, and a cache key that joined them would be the
 * first place they quietly became one number.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BikeAssignmentInput,
  BikeDetail,
  BikeOwnership,
  BikeFilters,
  BikeInput,
  BikeRegister,
  BikeUpdate,
  HirePurchaseBoard,
  HirePurchaseFilters,
  HirePurchaseInput,
  HirePurchasePaymentInput,
  HirePurchaseUpdate,
  HirePurchaseView,
  RemittanceAgreementInput,
  RemittanceBoard,
  RemittanceChargeInput,
  RemittanceCorrectionInput,
  RemittanceSettingsInput,
  RemittanceReceiptInput,
  RemittanceWeekDetail,
  RemittanceWeekSheet,
  RemittancePaymentInput,
  Business,
  RemittanceRow,
  VehicleCategory,
  VehicleRateInput,
  VehicleRateView,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

// ── Bikes ───────────────────────────────────────────────────────────────────

export function useBikes(filters: BikeFilters = {}): UseQueryResult<BikeRegister> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'bikes', path(''), filters],
    queryFn: () => api.get<BikeRegister>(path(`/rider-finance/bikes${qs(filters)}`)),
    staleTime: 60_000,
  });
}

export function useBike(id: string | null): UseQueryResult<{ data: BikeDetail }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'bike', path(''), id],
    queryFn: () => api.get<{ data: BikeDetail }>(path(`/rider-finance/bikes/${id!}`)),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

/** Everything a write to a bike could have changed, including a live agreement. */
function useInvalidateBikes(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderFinance', 'bikes'] });
    await client.invalidateQueries({ queryKey: ['riderFinance', 'bike'] });
    // Who holds which bike, and its owner, show on the riders list too.
    await client.invalidateQueries({ queryKey: ['riderDirectory'] });
    await client.invalidateQueries({ queryKey: ['riderFinance', 'remittance'] });
  };
}

export function useCreateBike(): UseMutationResult<{ data: BikeDetail }, Error, BikeInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateBikes();
  return useMutation({
    mutationFn: (input: BikeInput) =>
      api.post<{ data: BikeDetail }>(path('/rider-finance/bikes'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateBike(): UseMutationResult<
  { data: BikeDetail },
  Error,
  { id: string; input: BikeUpdate }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateBikes();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.patch<{ data: BikeDetail }>(path(`/rider-finance/bikes/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useAssignBike(): UseMutationResult<
  { data: BikeDetail },
  Error,
  { id: string; input: BikeAssignmentInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateBikes();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.post<{ data: BikeDetail }>(path(`/rider-finance/bikes/${id}/assignments`), input),
    onSuccess: invalidate,
  });
}

export function useReleaseBike(): UseMutationResult<
  { data: BikeDetail },
  Error,
  { id: string; releasedOn: string; note?: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateBikes();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      api.post<{ data: BikeDetail }>(path(`/rider-finance/bikes/${id}/release`), body),
    onSuccess: invalidate,
  });
}

// ── Remittance ──────────────────────────────────────────────────────────────

export function useRemittanceBoard(
  periodId?: string,
  category?: VehicleCategory,
): UseQueryResult<RemittanceBoard> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'remittance', path(''), periodId ?? 'latest', category ?? 'all'],
    queryFn: () =>
      api.get<RemittanceBoard>(path(`/rider-finance/remittance${qs({ periodId, category })}`)),
    staleTime: 30_000,
  });
}

// ── What a kind of vehicle owes ─────────────────────────────────────────────

export function useVehicleRates(category?: VehicleCategory): UseQueryResult<VehicleRateView[]> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'vehicleRates', path(''), category ?? 'all'],
    queryFn: async () =>
      (await api.get<{ data: VehicleRateView[] }>(
        path(`/rider-finance/remittance/vehicle-rates${qs({ category })}`),
      )).data,
    staleTime: 30_000,
  });
}

/**
 * Set a default from a date.
 *
 * Invalidates the sheet as well as the rate list: the sheet shows what the
 * default says each driver owes today, so it is stale the moment one moves.
 */
export function useSetVehicleRate(): UseMutationResult<
  VehicleRateView,
  Error,
  VehicleRateInput
> {
  const path = useBusinessPath();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleRateInput) =>
      api.post<VehicleRateView>(path('/rider-finance/remittance/vehicle-rates'), input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['riderFinance', 'vehicleRates'] });
      await client.invalidateQueries({ queryKey: ['riderFinance', 'remittance'] });
    },
  });
}

/**
 * The business record, for the country and state the fees are priced against.
 *
 * Read here rather than from `useAuth`, whose membership summary deliberately
 * carries only what every screen needs. The same query key the settings screen
 * uses, so saving the state there refreshes this.
 */
export function useBusinessRecord(): UseQueryResult<Business> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['business', path('')],
    queryFn: () => api.get<Business>(path('')),
    staleTime: 60_000,
  });
}

/** Where the business operates, for pricing by state. */
export function useSetBusinessState(): UseMutationResult<unknown, Error, string | null> {
  const path = useBusinessPath();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (stateCode: string | null) => api.patch(path(''), { stateCode }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['riderFinance'] });
      await client.invalidateQueries({ queryKey: ['business'] });
    },
  });
}

function useInvalidateRemittance(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderFinance', 'remittance'] });
    // The Logistics report reads the same accruals and shows what is paid.
    await client.invalidateQueries({ queryKey: ['logistics'] });
  };
}

export function useRecordRemittancePayment(): UseMutationResult<
  { data: RemittanceRow },
  Error,
  { id: string; input: RemittancePaymentInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateRemittance();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.post<{ data: RemittanceRow }>(path(`/rider-finance/remittance/${id}/payments`), input),
    onSuccess: invalidate,
  });
}

export function useDeleteRemittancePayment(): UseMutationResult<
  { data: RemittanceRow },
  Error,
  string
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateRemittance();
  return useMutation({
    mutationFn: (paymentId: string) =>
      api.del<{ data: RemittanceRow }>(path(`/rider-finance/remittance/payments/${paymentId}`)),
    onSuccess: invalidate,
  });
}

export function useWaiveRemittance(): UseMutationResult<
  { data: RemittanceRow },
  Error,
  { id: string; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateRemittance();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      api.post<{ data: RemittanceRow }>(path(`/rider-finance/remittance/${id}/waive`), { reason }),
    onSuccess: invalidate,
  });
}

export function useUnwaiveRemittance(): UseMutationResult<{ data: RemittanceRow }, Error, string> {
  const path = useBusinessPath();
  const invalidate = useInvalidateRemittance();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ data: RemittanceRow }>(path(`/rider-finance/remittance/${id}/waive`)),
    onSuccess: invalidate,
  });
}

// ── The remittance week ─────────────────────────────────────────────────────
//
// Under the same ['riderFinance', 'remittance'] key as the sheet it replaces,
// so every remittance write refreshes the rows, the cards and any open detail
// panel together — the row a person just paid against never lags the totals.

export interface RemittanceWeekFilters {
  category?: VehicleCategory | undefined;
  country?: string | undefined;
  state?: string | undefined;
  branchId?: string | undefined;
  /** The sidebar's area, which the API narrows the whole request to. */
  scopeCountry?: string | undefined;
  scopeState?: string | undefined;
  ownership?: BikeOwnership | undefined;
  investorId?: string | undefined;
}

export function useRemittanceWeek(
  startsOn: string | undefined,
  filters: RemittanceWeekFilters = {},
  enabled = true,
): UseQueryResult<RemittanceWeekSheet> {
  const path = useBusinessPath();
  return useQuery({
    enabled,
    queryKey: ['riderFinance', 'remittance', 'week', path(''), startsOn ?? 'current', filters],
    queryFn: () =>
      api.get<RemittanceWeekSheet>(
        path(`/rider-finance/remittance/week${qs({ startsOn, ...filters })}`),
      ),
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}

export function useRemittanceWeekDetail(
  riderId: string | null,
  startsOn: string | undefined,
): UseQueryResult<RemittanceWeekDetail> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'remittance', 'detail', path(''), riderId, startsOn ?? 'current'],
    queryFn: async () =>
      (await api.get<{ data: RemittanceWeekDetail }>(
        path(`/rider-finance/remittance/week/riders/${riderId!}${qs({ startsOn })}`),
      )).data,
    enabled: riderId !== null,
    staleTime: 10_000,
  });
}

/** Everything a remittance write can change, including the drawer's expected cash. */
function useInvalidateWeek(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderFinance', 'remittance'] });
    await client.invalidateQueries({ queryKey: ['riderFinance', 'riders'] });
    await client.invalidateQueries({ queryKey: ['logistics'] });
    await client.invalidateQueries({ queryKey: ['accountability'] });
    await client.invalidateQueries({ queryKey: ['finance'] });
    // Remittance received is dashboard revenue, on the day it arrived.
    await client.invalidateQueries({ queryKey: ['dashboard'] });
    await client.invalidateQueries({ queryKey: ['dashboard-growth'] });
  };
}

export function useRecordReceipt(): UseMutationResult<
  { data: { receiptId: string; replayed: boolean } },
  Error,
  RemittanceReceiptInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (input) =>
      api.post<{ data: { receiptId: string; replayed: boolean } }>(
        path('/rider-finance/remittance/receipts'),
        input,
      ),
    onSuccess: invalidate,
  });
}

export function useVoidRemittancePayment(): UseMutationResult<
  { ok: true },
  Error,
  { receiptId: string | null; paymentId: string; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: ({ receiptId, paymentId, reason }) =>
      api.post<{ ok: true }>(
        path(
          receiptId
            ? `/rider-finance/remittance/receipts/${receiptId}/void`
            : `/rider-finance/remittance/payments/${paymentId}/void`,
        ),
        { reason },
      ),
    onSuccess: invalidate,
  });
}

export function useChangeRemittanceAmount(): UseMutationResult<
  { ok: true },
  Error,
  { id: string; amount: string; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      api.patch<{ ok: true }>(path(`/rider-finance/remittance/${id}/amount`), body),
    onSuccess: invalidate,
  });
}

export function useCorrectRemittancePayment(): UseMutationResult<
  { data: { receiptId: string; replayed: boolean; voidedReceiptId: string } },
  Error,
  { receiptId: string } & RemittanceCorrectionInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: ({ receiptId, ...body }) =>
      api.post<{ data: { receiptId: string; replayed: boolean; voidedReceiptId: string } }>(
        path(`/rider-finance/remittance/receipts/${receiptId}/correct`),
        body,
      ),
    onSuccess: invalidate,
  });
}

export function useChargeWeek(): UseMutationResult<{ data: { remittanceId: string } }, Error, RemittanceChargeInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (input) =>
      api.post<{ data: { remittanceId: string } }>(path('/rider-finance/remittance/weeks/charges'), input),
    onSuccess: invalidate,
  });
}

export function useSetDueDate(): UseMutationResult<{ ok: true }, Error, { id: string; dueOn: string; reason: string }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      api.patch<{ ok: true }>(path(`/rider-finance/remittance/${id}/due-date`), body),
    onSuccess: invalidate,
  });
}

export function useSetRemittanceSettings(): UseMutationResult<{ ok: true }, Error, RemittanceSettingsInput> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (input) => api.put<{ ok: true }>(path('/rider-finance/remittance/settings'), input),
    onSuccess: invalidate,
  });
}

export function usePostRemittanceWeek(): UseMutationResult<
  { data: { periodId: string; riders: number; accrued: string; refreshed: boolean } },
  Error,
  string
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (startsOn: string) =>
      api.post<{ data: { periodId: string; riders: number; accrued: string; refreshed: boolean } }>(
        path('/rider-finance/remittance/weeks/post'),
        { startsOn },
      ),
    onSuccess: invalidate,
  });
}

export function useSetRemittanceAgreement(): UseMutationResult<
  { data: { id: string } },
  Error,
  RemittanceAgreementInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (input) =>
      api.post<{ data: { id: string } }>(path('/rider-finance/remittance/agreements'), input),
    onSuccess: invalidate,
  });
}

export function useWaiveWeek(): UseMutationResult<unknown, Error, { id: string; reason: string }> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: ({ id, reason }) => api.post(path(`/rider-finance/remittance/${id}/waive`), { reason }),
    onSuccess: invalidate,
  });
}

export function useUnwaiveWeek(): UseMutationResult<unknown, Error, string> {
  const path = useBusinessPath();
  const invalidate = useInvalidateWeek();
  return useMutation({
    mutationFn: (id: string) => api.del(path(`/rider-finance/remittance/${id}/waive`)),
    onSuccess: invalidate,
  });
}

// ── Hire purchase ───────────────────────────────────────────────────────────

export function useAgreements(
  filters: HirePurchaseFilters = {},
): UseQueryResult<HirePurchaseBoard> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'hirePurchase', path(''), filters],
    queryFn: () =>
      api.get<HirePurchaseBoard>(path(`/rider-finance/hire-purchase${qs(filters)}`)),
    staleTime: 60_000,
  });
}

/**
 * A hire purchase write invalidates the agreements and the register — never
 * the remittance board.
 *
 * The register, because an agreement moves the bike's status. Not the
 * remittance board, because an instalment has nothing to do with what a rider
 * owes for this week, and a shared invalidation is how two ledgers start
 * behaving like one.
 */
function useInvalidateHirePurchase(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderFinance', 'hirePurchase'] });
    await client.invalidateQueries({ queryKey: ['riderFinance', 'bikes'] });
    await client.invalidateQueries({ queryKey: ['riderFinance', 'bike'] });
  };
}

export function useCreateAgreement(): UseMutationResult<
  { data: HirePurchaseView },
  Error,
  HirePurchaseInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateHirePurchase();
  return useMutation({
    mutationFn: (input: HirePurchaseInput) =>
      api.post<{ data: HirePurchaseView }>(path('/rider-finance/hire-purchase'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateAgreement(): UseMutationResult<
  { data: HirePurchaseView },
  Error,
  { id: string; input: HirePurchaseUpdate }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateHirePurchase();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.patch<{ data: HirePurchaseView }>(path(`/rider-finance/hire-purchase/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useRecordInstalment(): UseMutationResult<
  { data: HirePurchaseView },
  Error,
  { id: string; input: HirePurchasePaymentInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateHirePurchase();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.post<{ data: HirePurchaseView }>(
        path(`/rider-finance/hire-purchase/${id}/payments`),
        input,
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteInstalment(): UseMutationResult<
  { data: HirePurchaseView },
  Error,
  string
> {
  const path = useBusinessPath();
  const invalidate = useInvalidateHirePurchase();
  return useMutation({
    mutationFn: (paymentId: string) =>
      api.del<{ data: HirePurchaseView }>(
        path(`/rider-finance/hire-purchase/payments/${paymentId}`),
      ),
    onSuccess: invalidate,
  });
}
